import { useEffect, useState } from 'react'

import { api, type DeliveryDaily, type DeliveryReport, type LogEntry } from '../api'

const PERIODS = [
  { days: 1, title: '24 часа' },
  { days: 7, title: '7 дней' },
  { days: 30, title: '30 дней' },
  { days: 90, title: '90 дней' },
]

// Главный вопрос экрана — «сколько пушей ушло и на сколько устройств».
// Поэтому первыми идут цифры, а график только показывает, как они распределены
// по дням.
export function Delivery({ appId }: { appId: string }) {
  const [days, setDays] = useState(7)
  const [report, setReport] = useState<DeliveryReport | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setReport(null)
    api
      .delivery(appId, days)
      .then(setReport)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить сводку'))
  }, [appId, days])

  if (error) return <div className="notice error">{error}</div>

  const summary = report?.summary
  const ctr = summary && summary.sent > 0 ? ((summary.clicked / summary.sent) * 100).toFixed(1) : '0.0'

  return (
    <div className="stack">
      <div className="row">
        {PERIODS.map((p) => (
          <button key={p.days} className={days === p.days ? 'primary' : ''} onClick={() => setDays(p.days)}>
            {p.title}
          </button>
        ))}
      </div>

      {!report && <p className="muted">Загрузка…</p>}

      {summary && (
        <>
          <div className="grid">
            <Stat value={summary.sent} label="Пушей отправлено" />
            <Stat
              value={summary.devices}
              label="Устройств охвачено"
              note={summary.approximate ? 'сумма по дням' : 'уникальных'}
            />
            <Stat value={summary.received} label="Подтверждено доставок" />
            <Stat value={summary.clicked} label="Открытий" note={`CTR ${ctr}%`} />
            <Stat value={summary.failed} label="Ошибок доставки" />
          </div>

          {summary.approximate && (
            <div className="notice">
              Сырой журнал хранится {report.retention_days} дней. За больший период цифры берутся
              из суточных агрегатов, поэтому устройство, активное в разные дни, посчитано несколько раз.
            </div>
          )}

          <DailyChart points={summary.daily} />

          <div className="card table-wrap">
            <h2>По платформам</h2>
            <table>
              <thead>
                <tr>
                  <th>Платформа</th>
                  <th>Пушей</th>
                  <th>Устройств</th>
                  <th>Открытий</th>
                  <th>Ошибок</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_platform.map((p) => (
                  <tr key={p.platform}>
                    <td>{p.platform}</td>
                    <td>{p.sent}</td>
                    <td>{p.devices}</td>
                    <td>{p.clicked}</td>
                    <td>{p.failed}</td>
                  </tr>
                ))}
                {summary.by_platform.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      За период пушей не было.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card table-wrap">
            <h2>Устройства с наибольшим числом пушей</h2>
            <table>
              <thead>
                <tr>
                  <th>Устройство</th>
                  <th>external_id</th>
                  <th>Платформа</th>
                  <th>Пушей</th>
                  <th>Открытий</th>
                </tr>
              </thead>
              <tbody>
                {report.top_devices.map((d) => (
                  <tr key={d.subscription_id}>
                    <td className="mono">{d.subscription_id.slice(0, 8)}…</td>
                    <td>{d.external_id || '—'}</td>
                    <td>{d.type}</td>
                    <td>{d.sent}</td>
                    <td>{d.clicked}</td>
                  </tr>
                ))}
                {report.top_devices.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      Нет данных за период.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function Stat({ value, label, note }: { value: number; label: string; note?: string }) {
  return (
    <div className="card tile">
      <div className="value">{value.toLocaleString('ru-RU')}</div>
      <div className="label">{label}</div>
      {note && <div className="label muted">{note}</div>}
    </div>
  )
}

// Одна серия — отправленные пуши по дням. Остальные цифры дня показываются
// при наведении: вторая шкала в том же графике только путала бы.
function DailyChart({ points }: { points: DeliveryDaily[] }) {
  const [hover, setHover] = useState<DeliveryDaily | null>(null)
  if (points.length === 0) return null

  const max = Math.max(...points.map((p) => p.sent), 1)
  const formatDay = (day: string) =>
    new Date(day).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h2 style={{ margin: 0 }}>Пуши по дням</h2>
        <span className="muted" style={{ fontSize: 13 }}>
          максимум за день: {max.toLocaleString('ru-RU')}
        </span>
      </div>

      <div className="chart">
        {points.map((p) => (
          <button
            key={p.day}
            type="button"
            className="chart-bar"
            style={{ height: `${Math.max((p.sent / max) * 100, p.sent > 0 ? 3 : 0)}%` }}
            onMouseEnter={() => setHover(p)}
            onMouseLeave={() => setHover(null)}
            onFocus={() => setHover(p)}
            onBlur={() => setHover(null)}
            aria-label={`${formatDay(p.day)}: ${p.sent} пушей на ${p.devices} устройств`}
          />
        ))}
      </div>

      <div className="chart-axis">
        <span>{formatDay(points[0].day)}</span>
        <span>{formatDay(points[points.length - 1].day)}</span>
      </div>

      <div className="chart-readout">
        {hover ? (
          <>
            <strong>{formatDay(hover.day)}</strong> · пушей {hover.sent} · устройств {hover.devices} ·
            открытий {hover.clicked} · ошибок {hover.failed}
          </>
        ) : (
          <span className="muted">Наведите на столбец, чтобы увидеть цифры дня</span>
        )}
      </div>
    </div>
  )
}

const LOG_TYPES = [
  { value: '', title: 'Все события' },
  { value: 'sent', title: 'Отправлено' },
  { value: 'received', title: 'Доставлено' },
  { value: 'clicked', title: 'Открыто' },
  { value: 'failed', title: 'Ошибки' },
  { value: 'dropped', title: 'Мёртвые токены' },
]

// Журнал пушей: что именно случилось с конкретным устройством и когда.
export function DeliveryLog({ appId, subscriptionId }: { appId: string; subscriptionId?: string }) {
  const [type, setType] = useState('')
  const [entries, setEntries] = useState<LogEntry[]>([])
  const [next, setNext] = useState(0)
  const [error, setError] = useState('')

  async function load(before?: number) {
    try {
      const result = await api.log(appId, {
        type,
        subscription_id: subscriptionId,
        before_id: before,
        limit: 100,
      })
      setEntries(before ? (current) => [...current, ...result.entries] : result.entries)
      setNext(result.next_before)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить журнал')
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, subscriptionId, type])

  return (
    <div className="stack">
      {error && <div className="notice error">{error}</div>}

      <div className="row">
        <label>
          Событие
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {LOG_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.title}
              </option>
            ))}
          </select>
        </label>
        {subscriptionId && (
          <span className="badge">только устройство {subscriptionId.slice(0, 8)}…</span>
        )}
      </div>

      <div className="card table-wrap">
        <table>
          <thead>
            <tr>
              <th>Время</th>
              <th>Событие</th>
              <th>Устройство</th>
              <th>Рассылка</th>
              <th>Причина</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="muted">{new Date(e.ts).toLocaleString('ru-RU')}</td>
                <td>
                  <EventBadge type={e.type} />
                </td>
                <td className="mono">{e.subscription_id ? e.subscription_id.slice(0, 8) + '…' : '—'}</td>
                <td>{e.title || '—'}</td>
                <td className="muted">
                  {e.code ? (
                    <>
                      <code>{e.code}</code> {e.raw && <span style={{ fontSize: 12 }}>{e.raw}</span>}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  Записей нет.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {next > 0 && (
        <div className="row">
          <button onClick={() => load(next)}>Показать ещё</button>
        </div>
      )}
    </div>
  )
}

export function EventBadge({ type }: { type: string }) {
  switch (type) {
    case 'sent':
      return <span className="badge ok">отправлено</span>
    case 'received':
      return <span className="badge ok">доставлено</span>
    case 'clicked':
      return <span className="badge ok">открыто</span>
    case 'failed':
      return <span className="badge bad">ошибка</span>
    case 'dropped':
      return <span className="badge bad">мёртвый токен</span>
    default:
      return <span className="badge">{type}</span>
  }
}
